import { ComponentProps } from "react";

import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { categoryListMap } from "@/features/ranking/data/category-data";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { usePublicData } from "@/services/public-data";
import {
  UserLimitationsType,
  userLimitationsSchema,
} from "@/types/user-limitations";

import { NavbarLink } from "./shared/NavbarLink";
import { NavbarMenu } from "./shared/NavbarMenu";

type Props = ComponentProps<"div"> & {
  lang: Lang;
};

export const NavbarList = ({ lang, ...props }: Props) => {
  const { t } = useLang();
  const { profile } = useAuth();
  const currentUserName = profile?.name ?? "";
  const { data: userLimitations } = usePublicData<UserLimitationsType>({
    itemName: "user-limitations",
    schema: userLimitationsSchema,
  });

  const isHideMarketEarlyWarningIndicator = userLimitations?.[
    "my-navbar-hidden-link-market-early-warning-indicator"
  ]?.data?.user?.find((user: string) => currentUserName.startsWith(user));

  const isHideKnowledgeBaseLinks = userLimitations?.[
    "my-navbar-hidden-link-knoledge-base"
  ]?.data?.user?.find((user: string) => currentUserName.startsWith(user));

  const isHidePricingLink = userLimitations?.[
    "my-navbar-hidden-link-pricing"
  ]?.data?.user?.find((user: string) => currentUserName.startsWith(user));

  const shouldChangeMy13FLinkText = userLimitations?.[
    "my-navbar-change-my-13f-link-text"
  ]?.data?.user?.find((user: string) => currentUserName.startsWith(user));

  return (
    <div {...props} aria-label="navbar-links">
      <NavbarLink
        href={`/${lang}${links.myAnalysisSearch}`}
        badgeText="Beta"
        aria-label="go to analysis-search page"
      >
        {t.layout.navbar.analysis}
      </NavbarLink>
      <NavbarMenu
        name={t.layout.navbar.aiStockSelection}
        items={[
          {
            name: t.layout.navbar.screener,
            href: `/${lang}${links.myScreener({ country: "us" })}`,
            label: "go to us-screener page",
          },
          {
            name: t.layout.navbar.usStockRanking,
            href: `/${lang}${links.myRanking({
              country: "us",
              category: categoryListMap.us[0],
            })}`,
            label: "go to us-stock-ranking page",
          },
          {
            name: t.layout.navbar.twStockRanking,
            href: `/${lang}${links.myRanking({
              country: "tw",
              category: categoryListMap.tw[0],
            })}`,
            label: "go to tw-stock-ranking page",
          },
          {
            name: t.layout.navbar.myNotification,
            href: `/${lang}${links.myNotification}`,
            label: "go to notification page",
          },
        ]}
        label="ai-stock-selection pages"
      />
      <NavbarMenu
        name={t.layout.navbar.marketAnalysis}
        items={[
          {
            name: t.layout.navbar.marketAnalysisFearGreed,
            href: `/${lang}${links.myMarketAnalysisFearAndGreed}`,
            label: "go to market-analysis fear-and-greed page",
          },
          ...(isHideMarketEarlyWarningIndicator
            ? []
            : [
                {
                  name: t.layout.navbar.marketAnalysisWarningIndicator,
                  href: `/${lang}${links.myMarketAnalysisEarlyWarningIndicatorHistoricalSignal}`,
                  label:
                    "go to market-analysis market-early-warning-indicator historical-signal page",
                },
              ]),
          {
            name: shouldChangeMy13FLinkText
              ? t.layout.navbar.assetManager ?? t.layout.navbar.my13F
              : t.layout.navbar.my13F,
            href: `/${lang}${links.my13F}`,
            label: "go to 13F page",
          },
        ]}
        label="market-analysis pages"
      />
      <NavbarLink
        href={`/${lang}${links.myWinwingpt}`}
        aria-label="go to winwin-gpt page"
      >
        {t.layout.navbar.myWinwinGpt}
      </NavbarLink>
      {!isHideKnowledgeBaseLinks && (
        <NavbarMenu
          name={t.layout.navbar.knowledgeBase}
          items={[
            {
              name: t.layout.navbar.myMediaCenter,
              href: `/${lang}${links.myMediaCategory({
                category: "website-tutorials",
              })}`,
              label: "go to media-center page",
            },
            {
              name: t.layout.navbar.blog,
              href: `/${lang}${links.myBlog}`,
              label: "go to blog page",
            },
          ]}
          label="knowledge base pages"
        />
      )}
      {!isHidePricingLink && (
        <NavbarLink
          href={`/${lang}${links.stockMiningPricing}`}
          badgeText={t.layout.navbar.upgrade}
          aria-label="go to pricing page"
        >
          {t.layout.navbar.pricing}
        </NavbarLink>
      )}
    </div>
  );
};
